/* Sakina Jewelry: PDP bundle cards, name fields and gift add-on (Impact theme) */
(() => {
  const formatMoney = (cents, format) => {
    const value = (Number(cents) || 0) / 100;
    const withDelimiters = (num, decimals, thousands, decimal) => {
      const parts = num.toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.join(decimal);
    };
    return (format || '${{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return withDelimiters(value, 0, ',', '.');
        case 'amount_with_comma_separator': return withDelimiters(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDelimiters(value, 0, '.', ',');
        default: return withDelimiters(value, 2, ',', '.');
      }
    });
  };

  /* Story 1.3: names in Arabic, Urdu, Hindi and other scripts.
     The customer types the name in English (or straight in the script) and we show the
     spelling we will engrave, with other spellings one tap away. Only that spelling goes
     to the order, as "Name N", which the supplier (Sihi) copies.
     Suggestions: Google Input Tools transliteration (JSONP, no server needed). */
  const SCRIPTS = {
    arabic: { label: 'Arabic', code: 'ar', dir: 'rtl', font: 'Noto Naskh Arabic', re: /[\u0600-\u06FF\u0750-\u077F\uFB50-\uFDFF\uFE70-\uFEFF]/ },
    farsi: { label: 'Farsi', code: 'fa', dir: 'rtl', font: 'Noto Naskh Arabic', re: /[\u0600-\u06FF\uFB50-\uFDFF\uFE70-\uFEFF]/ },
    urdu: { label: 'Urdu', code: 'ur', dir: 'rtl', font: 'Noto Nastaliq Urdu', re: /[\u0600-\u06FF\uFB50-\uFDFF\uFE70-\uFEFF]/ },
    hindi: { label: 'Hindi', code: 'hi', dir: 'ltr', font: 'Noto Sans Devanagari', re: /[\u0900-\u097F]/ },
    bengali: { label: 'Bengali', code: 'bn', dir: 'ltr', font: 'Noto Sans Bengali', re: /[\u0980-\u09FF]/ },
    punjabi: { label: 'Punjabi', code: 'pa', dir: 'ltr', font: 'Noto Sans Gurmukhi', re: /[\u0A00-\u0A7F]/ }
  };
  // common names where the first automatic suggestion is not the usual spelling
  const PREFERRED = {
    ar: {
      layla: 'ليلى', laila: 'ليلى', leila: 'ليلى', sarah: 'سارة', sara: 'سارة', fatima: 'فاطمة', fatma: 'فاطمة',
      maryam: 'مريم', mariam: 'مريم', aisha: 'عائشة', ayesha: 'عائشة', khadija: 'خديجة', zainab: 'زينب',
      amina: 'أمينة', noor: 'نور', nour: 'نور', yasmin: 'ياسمين', yasmine: 'ياسمين', salma: 'سلمى', huda: 'هدى',
      iman: 'إيمان', sakina: 'سكينة', muhammad: 'محمد', mohammed: 'محمد', mohamed: 'محمد', ahmed: 'أحمد',
      ahmad: 'أحمد', ali: 'علي', omar: 'عمر', umar: 'عمر', yusuf: 'يوسف', youssef: 'يوسف', ibrahim: 'إبراهيم',
      hamza: 'حمزة', adam: 'آدم', hassan: 'حسن', hussein: 'حسين', khalid: 'خالد', mustafa: 'مصطفى',
      ruqaiyah: 'رقية', ruqayyah: 'رقية', ruqaya: 'رقية', ruqayah: 'رقية'
    }
  };
  const LATIN = /^[A-Za-z][A-Za-z'-]*$/;
  const scriptOf = (lang) => SCRIPTS[String(lang || '').trim().toLowerCase()];

  const loadScriptFont = (lang) => {
    const sc = scriptOf(lang);
    if (!sc || document.querySelector(`link[data-ml-font="${sc.code}"]`)) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.dataset.mlFont = sc.code;
    link.href = `https://fonts.googleapis.com/css2?family=${sc.font.replace(/ /g, '+')}:wght@400;600&display=swap`;
    document.head.appendChild(link);
  };

  const suggestCache = new Map();
  let jsonpSeq = 0;
  const suggest = (word, code) => {
    const key = `${code}:${word.toLowerCase()}`;
    if (suggestCache.has(key)) return suggestCache.get(key);
    const request = new Promise((resolve) => {
      const cb = `mlTranslit${++jsonpSeq}`;
      const tag = document.createElement('script');
      let timer;
      const done = (list) => {
        clearTimeout(timer);
        window[cb] = () => {};
        tag.remove();
        resolve(list);
      };
      timer = setTimeout(() => done(null), 4000);
      window[cb] = (data) => done(data && data[0] === 'SUCCESS' && data[1] && data[1][0] ? data[1][0][1] : null);
      tag.onerror = () => done(null);
      tag.src = `https://inputtools.google.com/request?text=${encodeURIComponent(word)}&itc=${code}-t-i0-und&num=4&ie=utf-8&oe=utf-8&cb=${cb}`;
      document.head.appendChild(tag);
    }).then((list) => {
      if (!list) { suggestCache.delete(key); return null; }
      // drop broken suggestions that start with a vowel sign (e.g. "ोमर" for Omar in Hindi)
      const clean = list.filter((x) => x && !/^\p{M}/u.test(x));
      const first = PREFERRED[code] && PREFERRED[code][word.toLowerCase()];
      return (first ? [first, ...clean.filter((x) => x !== first)] : clean).slice(0, 4);
    });
    suggestCache.set(key, request);
    return request;
  };

  // "Layla & Omar" -> [{word:'Layla'}, {sep:' & '}, {word:'Omar'}]
  const tokenize = (text) => text.trim().split(/(\s*[,&+/]\s*|\s+)/).filter(Boolean)
    .map((part) => (/^\s*[,&+/]?\s*$/.test(part) ? { sep: part } : { word: part }));

  const escapeHtml = (value) => String(value).replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);

  // spelling shown for each typed word: the customer's pick, else the top suggestion
  const readScript = (row) => {
    const sc = scriptOf(row.closest('[data-ml-qty]').dataset.lang);
    const state = row.mlScript;
    const latin = state.tokens.filter((t) => t.word && LATIN.test(t.word));
    const loading = latin.some((t) => state.options[t.word.toLowerCase()] === undefined);
    const missing = latin.some((t) => { const list = state.options[t.word.toLowerCase()]; return list !== undefined && !(list && list.length); });
    const parts = state.tokens.map((t) => {
      if (t.sep) return t.sep;
      const key = t.word.toLowerCase();
      if (LATIN.test(t.word)) return state.picks[key] || (state.options[key] && state.options[key][0]) || null;
      return sc.re.test(t.word) && !/[A-Za-z0-9]/.test(t.word) ? t.word : null;
    });
    return { sc, state, latin, loading, missing, native: !latin.length, text: parts.every((x) => x !== null) ? parts.join('') : null };
  };

  // what the supplier copies: the spelling shown to the customer, or what she typed
  // while suggestions are still loading or when none were found
  const syncScriptOut = (row) => {
    const out = row.querySelector('[data-ml-script-out]');
    if (!out) return;
    const field = row.querySelector('[data-ml-name]');
    const typed = field.value.trim();
    const active = Boolean(typed && !row.hidden && !field.disabled);
    out.disabled = !active;
    if (!active) return;
    const view = scriptOf(row.closest('[data-ml-qty]').dataset.lang) && row.mlScript ? readScript(row) : null;
    out.value = (view && view.text) || typed;
  };

  const renderScript = (box, row, refocus) => {
    const panel = row.querySelector('[data-ml-script]');
    if (!panel) return;
    const sc = scriptOf(box.dataset.lang);
    if (!sc || !row.mlScript) { panel.hidden = true; panel.innerHTML = ''; syncScriptOut(row); return; }
    const view = readScript(row);
    const { state } = view;
    const font = `style="font-family:'${sc.font}',serif" lang="${sc.code}" dir="${sc.dir}"`;
    let html;
    if (view.native) {
      html = view.text
        ? `<p class="ml-script__label">We will engrave</p><p class="ml-script__preview" ${font}>${escapeHtml(view.text)}</p><p class="ml-script__note">Exactly as you typed it, checked letter by letter before production.</p>`
        : `<p class="ml-script__note">We will check this spelling letter by letter before production.</p>`;
    } else if (view.loading) {
      html = `<p class="ml-script__note">Finding the ${sc.label} spelling…</p>`;
    } else if (view.missing || !view.text) {
      html = `<p class="ml-script__note">We will write the ${sc.label} spelling for you before production.</p>`;
    } else {
      const seen = new Set();
      const words = view.latin.filter((t) => !seen.has(t.word.toLowerCase()) && seen.add(t.word.toLowerCase()));
      const alts = words.map((t) => {
        const key = t.word.toLowerCase();
        const current = state.picks[key] || state.options[key][0];
        const chips = state.options[key].map((opt) => `<button type="button" role="radio" class="ml-script__opt" ${font} aria-checked="${current === opt}" data-ml-pick="${escapeHtml(opt)}" data-word="${escapeHtml(key)}">${escapeHtml(opt)}</button>`).join('');
        return `<div class="ml-script__word"><span class="ml-script__src">${escapeHtml(t.word)}</span><div class="ml-script__opts" role="radiogroup" aria-label="${sc.label} spelling for ${escapeHtml(t.word)}">${chips}</div></div>`;
      }).join('');
      html = `<p class="ml-script__label">We will engrave</p>
        <p class="ml-script__preview" ${font}>${escapeHtml(view.text)}</p>
        <button type="button" class="ml-script__more" data-ml-more aria-expanded="${Boolean(state.open)}">${state.open ? 'Hide other spellings' : 'Not quite right? See other spellings'}</button>
        <div class="ml-script__alts"${state.open ? '' : ' hidden'}>${alts}</div>`;
    }
    panel.innerHTML = html;
    panel.hidden = false;
    if (refocus) {
      const target = panel.querySelector(refocus);
      if (target) target.focus();
    }
    syncScriptOut(row);
  };

  const nameTimers = new WeakMap();
  const onNameInput = (box, row, reset) => {
    if (!row || !row.querySelector('[data-ml-script]')) return;
    const sc = scriptOf(box.dataset.lang);
    const text = row.querySelector('[data-ml-name]').value;
    const previous = !reset && row.mlScript;
    row.mlScript = text.trim() ? {
      tokens: tokenize(text),
      picks: previous ? previous.picks : {},
      options: previous ? previous.options : {},
      open: previous ? previous.open : false
    } : null;
    renderScript(box, row);
    clearTimeout(nameTimers.get(row));
    if (!sc || !row.mlScript) return;
    nameTimers.set(row, setTimeout(() => fetchSuggestions(box, row), 350));
  };

  const fetchSuggestions = (box, row) => {
    clearTimeout(nameTimers.get(row));
    const sc = scriptOf(box.dataset.lang);
    const state = row.mlScript;
    if (!sc || !state) return Promise.resolve();
    return Promise.all(state.tokens.filter((t) => t.word && LATIN.test(t.word) && state.options[t.word.toLowerCase()] === undefined).map((t) => {
      const key = t.word.toLowerCase();
      return suggest(t.word, sc.code).then((list) => {
        if (row.mlScript !== state || scriptOf(box.dataset.lang) !== sc) return;
        state.options[key] = list;
        renderScript(box, row);
      });
    }));
  };

  // "Add to bag" right after typing: wait (max 3s) for the spelling, so the order never
  // gets the English name on a product engraved in another script
  const releasedForms = new WeakSet();
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (releasedForms.has(form)) { releasedForms.delete(form); return; }
    const formId = form.getAttribute && form.getAttribute('id'); // form.id is shadowed by the variant "id" input
    const box = formId && document.querySelector(`[data-ml-qty][data-form="${CSS.escape(formId)}"]`);
    if (!box || !scriptOf(box.dataset.lang)) return;
    const rows = [...box.querySelectorAll('[data-name-row]')].filter((row) => !row.hidden && row.mlScript && readScript(row).loading);
    if (!rows.length) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const timeout = new Promise((resolve) => setTimeout(resolve, 3000));
    Promise.race([Promise.all(rows.map((row) => fetchSuggestions(box, row))), timeout]).then(() => {
      releasedForms.add(form);
      if (form.requestSubmit) form.requestSubmit(); else form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    });
  }, true);

  // "See other spellings" and the spelling chips
  const onScriptClick = (box, button) => {
    const row = button.closest('[data-name-row]');
    const state = row && row.mlScript;
    if (!state) return;
    let refocus;
    if (button.hasAttribute('data-ml-more')) {
      state.open = !state.open;
      refocus = '[data-ml-more]';
    } else if (button.dataset.mlPick) {
      state.picks[button.dataset.word] = button.dataset.mlPick;
      refocus = `[data-ml-pick="${CSS.escape(button.dataset.mlPick)}"][data-word="${CSS.escape(button.dataset.word)}"]`;
    }
    renderScript(box, row, refocus);
  };

  const renderQty = (box) => {
    const price = Number(box.dataset.price);
    const compare = Math.max(Number(box.dataset.compare), price);
    const money = box.dataset.money;
    box.querySelectorAll('.ml-qty__card').forEach((card) => {
      const radio = card.querySelector('input[type="radio"]');
      const qty = Number(radio.value);
      const pct = Number(radio.dataset.pct) || 0;
      const now = Math.round(price * qty * (100 - pct) / 100);
      const was = compare * qty;
      card.querySelector('[data-now]').textContent = formatMoney(now, money);
      card.querySelector('[data-was]').textContent = was > now ? formatMoney(was, money) : '';
      card.classList.toggle('is-active', radio.checked);
    });
    setAtcPrice(box);
  };

  // "Add to bag · $63.84": the button shows the total for the chosen quantity (Noor style)
  const setAtcPrice = (box) => {
    const button = document.querySelector(`buy-buttons[form="${box.dataset.form}"] button[type="submit"]`);
    if (!button || button.disabled) return;
    const label = button.getAttribute('is') === 'custom-button' && button.firstElementChild ? button.firstElementChild : button;
    const checked = box.querySelector('input[type="radio"]:checked');
    const qty = checked ? Number(checked.value) : 1;
    const pct = checked ? Number(checked.dataset.pct) || 0 : 0;
    const total = Math.round(Number(box.dataset.price) * qty * (100 - pct) / 100);
    const base = (window.themeVariables && window.themeVariables.strings && window.themeVariables.strings.addToCartButton) || 'Add to bag';
    label.textContent = `${base} · ${formatMoney(total, box.dataset.money)}`;
  };

  const applyQty = (box) => {
    const checked = box.querySelector('input[type="radio"]:checked');
    const qty = checked ? Number(checked.value) : 1;
    const input = box.querySelector('[data-ml-qty-input]');
    if (input) input.value = qty;
    box.querySelectorAll('[data-name-row]').forEach((row) => {
      const visible = Number(row.dataset.nameRow) <= qty;
      const field = row.querySelector('[data-ml-name]');
      row.hidden = !visible;
      field.disabled = !visible;
      field.required = visible;
      syncScriptOut(row);
    });
    renderQty(box);
  };

  const init = (root = document) => {
    root.querySelectorAll('[data-ml-qty]').forEach((box) => {
      if (box.dataset.ready) return;
      box.dataset.ready = '1';
      box.addEventListener('change', (event) => {
        if (event.target.matches('input[type="radio"]')) applyQty(box);
      });
      box.addEventListener('input', (event) => {
        if (event.target.matches('[data-ml-name]')) onNameInput(box, event.target.closest('[data-name-row]'));
      });
      box.addEventListener('click', (event) => {
        const button = event.target.closest('[data-ml-pick], [data-ml-more]');
        if (button) onScriptClick(box, button);
      });
      if (box.dataset.lang) loadScriptFont(box.dataset.lang);
      applyQty(box);
    });
  };

  document.addEventListener('variant:change', (event) => {
    const variant = event.detail && event.detail.variant;
    if (!variant) return;
    document.querySelectorAll('[data-ml-qty]').forEach((box) => {
      box.dataset.price = variant.price;
      box.dataset.compare = variant.compare_at_price || variant.price;
      renderQty(box);
      const pos = Number(box.dataset.langPos) || 0;
      const lang = pos && variant.options ? variant.options[pos - 1] : '';
      if (lang && lang.toLowerCase() !== (box.dataset.lang || '').toLowerCase()) {
        box.dataset.lang = lang;
        loadScriptFont(lang);
        const sc = scriptOf(lang);
        if (sc) box.querySelectorAll('[data-ml-name-hint]').forEach((hint) => { hint.textContent = `in English or ${sc.label}`; });
        box.querySelectorAll('[data-name-row]').forEach((row) => onNameInput(box, row, true));
      }
    });
  });

  let giftPending = false;
  document.addEventListener('variant:add', async () => {
    const gift = document.querySelector('[data-ml-gift]');
    const check = gift && gift.querySelector('[data-ml-gift-check]');
    if (!check || !check.checked || giftPending) return;
    giftPending = true;
    try {
      await fetch(`${window.Shopify && Shopify.routes ? Shopify.routes.root : '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(gift.dataset.variant), quantity: 1 }] })
      });
      check.checked = false;
      (window.mlRefreshCart ? window.mlRefreshCart() : document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true })));
    } finally {
      giftPending = false;
    }
  });

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-ml-quick-add]');
    if (!button) return;
    if (button.dataset.url) {
      window.location.href = button.dataset.url;
      return;
    }
    button.disabled = true;
    try {
      const response = await fetch(`${window.Shopify && Shopify.routes ? Shopify.routes.root : '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(button.dataset.mlQuickAdd), quantity: 1 }] })
      });
      if (response.ok) {
        button.textContent = 'Added';
        if (window.mlRefreshCart) await window.mlRefreshCart();
        else document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        if (window.mlOpenCart) window.mlOpenCart();
      }
    } finally {
      button.disabled = false;
    }
  });


  // keep the sticky gallery just below the sticky header, whatever its height
  const setHeaderH = () => {
    const header = document.querySelector('.shopify-section--ml-header');
    if (header) document.documentElement.style.setProperty('--ml-header-h', `${Math.round(header.getBoundingClientRect().height)}px`);
  };
  setHeaderH();
  window.addEventListener('resize', setHeaderH);

  // customer videos: load and play muted only while on screen, pause when scrolled away
  const initUgc = (root = document) => {
    const vids = root.querySelectorAll('.ml-pdp-ugc__vid');
    if (!vids.length) return;
    const play = (v) => {
      if (!v.src) v.src = v.dataset.src;
      const p = v.play();
      if (p && p.catch) p.catch(() => {});
    };
    if (!('IntersectionObserver' in window)) { vids.forEach(play); return; }
    const io = new IntersectionObserver((entries) => entries.forEach((e) => {
      if (e.isIntersecting) play(e.target); else e.target.pause();
    }), { rootMargin: '200px 0px', threshold: 0.15 });
    vids.forEach((v) => io.observe(v));
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initUgc());
  else initUgc();
  document.addEventListener('shopify:section:load', (event) => initUgc(event.target));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
